import { Calculation } from '@carbon/react/icons';
import { createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';

import { CalculationIconTrigger } from 'components/calculation-icon-trigger';
import { DataTable, type DataTableColumnDef } from 'components/data-table';
import { LazyUnitInterestCard } from 'components/lazy-unit-interest-card/lazy-unit-interest-card';
import { Link } from 'components/link';
import { GisCalculationCard } from 'components/uat-nri-matrix-card';
import { UatNriMatrixCard } from 'components/uat-nri-matrix-card/uat-nri-matrix-card';
import { tagsTextColumn } from 'lib/asset-aliases';
import { format8 } from 'utils/number';

import type { SlotInterestRow } from '../types';
import {
  evidenceSourceLabel,
  interestSourceLabel,
  STATUS_DISPLAY,
  STREAM_LABELS,
  isAmSourceSelectable,
  subtractActRows,
  subtractNriCell,
  type SubtractActRow,
} from '../utils';
import styles from './subtracts-table.module.scss';

/**
 * Trigger render-prop for the per-subtract AM NRI / AM WI cells —
 * `CalculationIconTrigger` wrapped around Carbon's `Calculation` icon. Mirrors
 * the pattern at `components/nri-table/utils.tsx`.
 *
 * @param {boolean} active - Whether the formula card is currently open.
 * @param {Function} toggle - Toggles the card open/closed.
 * @return {JSX.Element} The icon trigger element.
 */
const renderCalculationTrigger = (
  active: boolean,
  toggle: () => void
): JSX.Element => (
  <CalculationIconTrigger
    definition="Show Calculation"
    active={active}
    onClick={toggle}
  >
    <div className={styles.iconContainer}>
      <Calculation aria-hidden="true" role="img" />
    </div>
  </CalculationIconTrigger>
);

interface SubtractsTableProps {
  slot: SlotInterestRow;
}

type SubtractRow = SubtractActRow & Record<string, unknown>;

const columnHelper = createColumnHelper<SubtractRow>();

/**
 * Nested table rendered inside each slot row's expansion slot. One row per
 * subtract with identity (Tract / Subtract / UAT pipe-joined), the slot-level
 * source labels, the per-subtract Certified Interest breakdown, and AM NRI /
 * AM WI per subtract.
 *
 * Wrapped in the project's `DataTable` so headers, alignment, and cell
 * formatting match the parent table. All interactive chrome (toolbar, search,
 * filters, selection, sort, footer) is disabled — read-only view of a small
 * static row set.
 *
 * Columns are built inside the component (via `useMemo` keyed on
 * `slot.confirmation`) so the source-label and Certified Interest cells can
 * close over the slot's confirmation without grafting it onto every row.
 *
 * @param {SubtractsTableProps} props - Component props.
 * @param {SlotInterestRow} props.slot - Parent slot (supplies subtracts + confirmation).
 * @return {JSX.Element} The rendered nested DataTable.
 */
export const SubtractsTable = ({ slot }: SubtractsTableProps): JSX.Element => {
  /**
   * Builds one of the four NRI/WI value columns, mirroring the parent table's
   * AM NRI / GIS NRI split — see `subtractNriCell` for which value (and
   * popovers) each cell shows.
   *
   * @param {boolean} amSlot - Whether the slot's unit has an Allocation
   *   Manager (passed in, not closed over, so it's the memo's only input).
   * @param {'am' | 'gis'} source - Which column pair this column belongs to.
   * @param {'NRI' | 'WI'} calculationType - Which value/popovers to render.
   * @return {object} The TanStack column definition.
   */
  const buildNriCalcColumn = (
    amSlot: boolean,
    source: 'am' | 'gis',
    calculationType: 'NRI' | 'WI'
  ) =>
    columnHelper.display({
      // amNri / gisNri / amWi / gisWi — "AM NRI" / "GIS NRI" / …
      id: `${source}${calculationType === 'NRI' ? 'Nri' : 'Wi'}`,
      header: `${source.toUpperCase()} ${calculationType}`,
      size: 220,
      meta: {
        type: 'decimal',
        format: { minDecimalPlaces: 8, maxDecimalPlaces: 8 },
      },
      cell: (info) => {
        const cell = subtractNriCell(
          amSlot,
          info.row.original,
          source,
          calculationType
        );
        if (cell === null) return '—';
        const { nriCalculationId } = info.row.original;
        return (
          <span className={styles.amCell}>
            <span>{format8(cell.value, '—')}</span>
            {nriCalculationId !== null && (
              <>
                {cell.formula === 'gis' ? (
                  <GisCalculationCard
                    nriCalculationId={nriCalculationId}
                    calculationType={calculationType}
                    renderTrigger={renderCalculationTrigger}
                  />
                ) : (
                  <LazyUnitInterestCard
                    nriCalculationId={nriCalculationId}
                    unitInterestValue={cell.value}
                    calculationType={calculationType}
                    renderTrigger={renderCalculationTrigger}
                  />
                )}
                <UatNriMatrixCard
                  nriCalculationId={nriCalculationId}
                  calculationType={calculationType}
                  highlightGisRow={source === 'gis'}
                />
              </>
            )}
          </span>
        );
      },
    });

  const amSlot = isAmSourceSelectable(slot);

  const columns = useMemo(
    () =>
      [
        columnHelper.display({
          id: 'identity',
          header: 'Tract / Subtract / UAT',
          size: 560,
          cell: (info) => {
            const {
              tractId,
              tractName,
              tractHref,
              subtractName,
              subtractHref,
              uatName,
            } = info.row.original;
            return (
              <span className={styles.identityRow}>
                <span>
                  Tract:{' '}
                  {tractHref === null ? (
                    (tractName ?? tractId ?? '—')
                  ) : (
                    <Link to={tractHref} openInNewTab>
                      {tractName ?? tractId}
                    </Link>
                  )}
                </span>
                <span className={styles.identitySep}>|</span>
                <span>
                  Subtract:{' '}
                  <Link to={subtractHref} openInNewTab>
                    {subtractName}
                  </Link>
                </span>
                <span className={styles.identitySep}>|</span>
                <span>UAT: {uatName ?? '—'}</span>
              </span>
            );
          },
        }),
        // The tract's tags as comma text, `--` with none (INS-13689).
        tagsTextColumn<SubtractRow>({
          accessorKey: 'tractTags',
          id: 'tractTags',
          header: 'Tract Tags',
          size: 160,
        }),
        // One row per certification act (INS-9897): Certified for a confirmation
        // group, On Hold for the held streams, Not Started for an uncertified
        // subtract. Icons match the main table's status column.
        columnHelper.display({
          id: 'tractStatus',
          header: 'Status',
          size: 150,
          cell: (info) => {
            const { actKind } = info.row.original;
            const { Icon, label } = STATUS_DISPLAY[actKind];
            const className = {
              certified: styles.iconCertified,
              on_hold: styles.iconOnHold,
              not_started: styles.iconNotStarted,
            }[actKind];
            return (
              <span className={styles.statusCell}>
                <Icon size={16} className={className} />
                {label}
              </span>
            );
          },
        }),

        // The streams this act covers — the group's certified streams, the held
        // streams, or "—" when Not Started.
        columnHelper.display({
          id: 'certifiedStreams',
          header: 'Stream',
          size: 140,
          enableSorting: false,
          cell: (info) => {
            const { actStreams } = info.row.original;
            if (actStreams.length === 0) return '—';
            return actStreams.map((s) => STREAM_LABELS[s] ?? s).join(', ');
          },
        }),
        columnHelper.display({
          id: 'interestSource',
          header: 'Interest Source',
          size: 180,
          cell: (info) => {
            const { actSource } = info.row.original;
            return interestSourceLabel(
              actSource ? { source: actSource } : null
            );
          },
        }),
        columnHelper.display({
          id: 'evidenceSource',
          header: 'Evidence Source',
          size: 180,
          cell: (info) => {
            const { actSource } = info.row.original;
            return evidenceSourceLabel(
              actSource ? { source: actSource } : null
            );
          },
        }),
        columnHelper.display({
          id: 'certifiedInterest',
          header: 'Certified Interest',
          size: 200,
          cell: (info) => {
            const { actNri, actWi } = info.row.original;
            if (actNri === null && actWi === null) return '—';
            return (
              <div className={styles.certifiedStack}>
                <span>NRI: {format8(actNri, '—')}</span>
                <span>WI: {format8(actWi, '—')}</span>
              </div>
            );
          },
        }),
        // AM NRI / AM WI / GIS NRI / GIS WI mirror the parent table's column
        // set: AM values only on AM-backed slots, GIS values on every slot.
        buildNriCalcColumn(amSlot, 'am', 'NRI'),
        buildNriCalcColumn(amSlot, 'gis', 'NRI'),
        buildNriCalcColumn(amSlot, 'am', 'WI'),
        buildNriCalcColumn(amSlot, 'gis', 'WI'),
        columnHelper.display({
          id: 'confirmedBy',
          header: 'Confirmed By',
          size: 160,
          cell: (info) => info.row.original.actConfirmedByName ?? '—',
        }),
        columnHelper.display({
          id: 'confirmedAt',
          header: 'Confirmed At',
          size: 140,
          cell: (info) => {
            const confirmedAt = info.row.original.actConfirmedAt;
            return confirmedAt
              ? new Date(confirmedAt).toLocaleDateString('en-CA')
              : '—';
          },
        }),
      ] as DataTableColumnDef<SubtractRow>[],
    // recreated per render but only depends on amSlot
    [amSlot]
  );

  // One row per certification act; rows with a Certified Interest value carry
  // `numberOfLines: 2` so the stacked `NRI: …` / `WI: …` cell fits — the same
  // contract the parent comparison-table uses for confirmed slot rows.
  const data = useMemo<SubtractRow[]>(
    () => subtractActRows(slot) as SubtractRow[],
    [slot]
  );

  return (
    <div className={styles.tableWrapper}>
      <DataTable<SubtractRow>
        data={data}
        columns={columns}
        getRowId={(row) => row.rowId}
        config={{
          columnResizing: false,
          columnReordering: false,
          columnPinning: false,
          columnVisibility: false,
          columnFilters: false,
          sorting: false,
          globalSearch: false,
          hideFooter: true,
          fitContent: true,
        }}
      />
    </div>
  );
};
